import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Save, Activity, Box, MapPin, Clock, Trash2, FileText, Search, Briefcase, Workflow, Zap, User, ChevronLeft, ChevronRight, Type, Minus, Plus, Paperclip } from 'lucide-react';
import api from './axiosConfig';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import UniversalLookup from './UniversalLookup';
import { usePermissions } from './usePermissions';
import { useFontSize } from './FontSizeContext';
import WorkflowActionModal from './WorkflowActionModal';
import WorkflowHistoryModal from './WorkflowHistoryModal';
import AttachmentsModal from './AttachmentsModal'; // 🎯 استدعاء مودال المرفقات

const IncidentDetails = () => {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const location = useLocation();
  
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', field: '', filters: {}, onCustomSelect: null });
  const [nav, setNav] = useState({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 }); 

  const user = JSON.parse(localStorage.getItem('user'));
  const sessionSite = user?.site_id || '';
  const sessionOrg = user?.org_id || '';
  
  const [isWFHistoryOpen, setIsWFHistoryOpen] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false); // 🎯 State للمرفقات
  const [count, setCount] = useState(0); // 🎯 State لعدد المرفقات

  const { canCreate, canUpdate, canDelete } = usePermissions('incidents');
  const [wfContext, setWfContext] = useState({ can_start: false, can_action: false });

  // --- مكون التحكم في الخط ---
  const FontControls = () => {
    return (
      <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full border border-white/20 ml-4">
        <button onClick={() => fontContext.changeFontSize(-1)} className="hover:text-orange-400 transition-colors"><Minus size={14} /></button>
        <div className="flex items-center gap-1 min-w-[40px] justify-center text-blue-200">
          <Type size={14} /><span className="font-black text-[10px]">{fontSize}</span>
        </div>
        <button onClick={() => fontContext.changeFontSize(1)} className="hover:text-orange-400 transition-colors"><Plus size={14} /></button>
      </div>
    );
  };

  const fetchIncident = useCallback(async () => {
    try {
      if (isNew) {
        let nextNum = '';
        try {
          // 🎯 محاولة جلب رقم البلاغ التلقائي لو الباك إند بيدعمها
          const resNum = await api.get(`/incidents/next-number/${sessionOrg}`);
          nextNum = resNum.data.next_incidentnum || resNum.data.ticket_id || '';
        } catch(e) { console.log("Next number API not found, will rely on backend generation on save."); }

        setIncident({
          ticket_id: nextNum,
          description: '',
          long_description: '',
          priority: 3,
          assetnum: null,
          location_id: null,
          wonum: null,
          status:'NEW',
          site_id: sessionSite,
          org_id: sessionOrg,
          reported_by: user?.username || 'Unknown',
          report_date: new Date().toISOString()
        });
      } else {
        const res = await api.get(`/incidents/${id}`);
        setIncident(res.data);
      }
    } catch (err) {
      toast.error(t('common.load_error', "Error loading incident data"));
    } finally {
      setLoading(false);
    }
  }, [id, isNew, sessionSite, sessionOrg, user?.username, t]);

  useEffect(() => { fetchIncident(); }, [fetchIncident]);

  // 🎯 دالة جلب عدد المرفقات
  const fetchCount = useCallback(async () => {
    if (isNew || !incident?.ticket_id) return;
    try {
      const res = await api.get(`/attachments/count/INCIDENT/${incident.ticket_id}`);
      setCount(res.data.count || 0);
    } catch (err) { console.error("Error fetching attachment count:", err); }
  }, [incident?.ticket_id, isNew]);

  useEffect(() => { fetchCount(); }, [fetchCount]);

  const loadNavigation = useCallback(async () => {
    if (isNew) return;
    try {
      const token = localStorage.getItem('token'); 
      const res = await axios.get(`/api/incidents/${id}/navigation${location.search}`, { 
        headers: { 'Authorization': `Bearer ${token}` } 
      });
      setNav(res.data || { first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });
    } catch (err) { 
      console.error("Nav failed", err); 
    }
  }, [id, isNew, location.search]);

  useEffect(() => { loadNavigation(); }, [loadNavigation]);

  const goTo = (targetId) => targetId && navigate(`/incidents/${targetId}${location.search}`);

  const handleSmartSearch = (fieldName, title, defaultType, onCustomSelect = null) => {
    let extraFilters = { site_id: sessionSite, org_id: sessionOrg };
    setLookup({ 
      isOpen: true, title, type: defaultType, domainId: '', field: fieldName, filters: extraFilters, onCustomSelect: onCustomSelect 
    });
  };

  const handleInputChange = (field, value) => {
    setIncident(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    const loadId = toast.loading(t('common.saving', "Saving..."));
    try {
      if (isNew) {
        const res = await api.post(`/incidents/create`, incident);
        toast.success(t('common.save_success', "Incident Created"), { id: loadId });
        navigate(`/incidents/${res.data.id}${location.search}`);
      } else {
        await api.put(`/incidents/${id}`, incident);
        toast.success(t('common.save_success', "Incident Updated"), { id: loadId });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.save_fail', "Update failed"), { id: loadId });
    }
  };

  const handleDelete = async (id, incidentNum) => {
    const result = await Swal.fire({
      title: t('common.are_you_sure', 'Are you sure?'),
      text: t('incidents.details.delete_warn', { ticket: incidentNum }, `You are about to permanently delete Incident ${incidentNum}. This action cannot be undone!`),
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', cancelButtonColor: '#3085d6',
      confirmButtonText: t('common.yes_delete', 'Yes, Delete it!'),
      cancelButtonText: t('common.cancel', 'Cancel'),
      customClass: { popup: `${isRTL ? 'font-cairo' : 'font-sans'} text-[12px]` }
    });

    if (!result.isConfirmed) return;

    const loadId = toast.loading(t('common.deleting', "Processing deletion..."));

    try {
      const res = await api.delete(`/incidents/${id}`);
      if (res.data.status === 'success') {
        toast.success(t('common.delete_success', "Incident Deleted Successfully"), { id: loadId });
        navigate(`/incidents${location.search}`); 
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || t('common.delete_fail', "Delete operation failed");
      toast.error(errorMsg, { id: loadId });
    }
  };

  const handleRouteWorkflow = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/workflow/initiate', {
        module: 'INCIDENT', ref_id: id
      }, { headers: { 'Authorization': `Bearer ${token}` } });
      
      toast.success(t('common.wf_initiated', "Workflow Routed Successfully! 🚀"));
      fetchIncident(); 
      fetchWFStatus();
    } catch (err) { toast.error(t('common.wf_fail', "Failed to route workflow")); }
  };

  const fetchWFStatus = async () => {
    try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`/api/workflow/check-runtime/INCIDENT/${id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        setWfContext(res.data);
    } catch (err) { console.error("WF Check Failed", err); }
  };

  useEffect(() => {
    if (!isNew) fetchWFStatus();
  }, [id, incident?.status]);

  if (loading) return <div className="p-20 text-center animate-pulse font-black text-[#005495]">{t('common.loading', 'LOADING TICKET CONTEXT...')}</div>;

  return (
    <div className={`flex flex-col h-screen bg-[#f3f4f6] uppercase tracking-tighter duration-300 transition-[font-size] ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>
      <UniversalLookup 
        {...lookup} 
        onClose={() => setLookup({...lookup, isOpen: false})} 
        onSelect={(row) => {
          if (lookup.onCustomSelect) {
            lookup.onCustomSelect(row);
          } else {
            const val = row.assetnum || row.location_id || row.itemnum || row.meter_num || row.value || row.id || row.site_id;
            handleInputChange(lookup.field, val);
          }
          setLookup(prev => ({ ...prev, isOpen: false }));
        }} 
      />
      
      {isWFHistoryOpen && (
        <WorkflowHistoryModal module="INCIDENT" refId={id} onClose={() => setIsWFHistoryOpen(false)} />
      )}

      {/* 🎯 مودال المرفقات */}
      {isDocModalOpen && !isNew && (
        <AttachmentsModal 
          isOpen={isDocModalOpen} 
          onClose={() => {setIsDocModalOpen(false); fetchCount();}} 
          ownertable="INCIDENT" 
          ownerid={incident?.ticket_id} 
        />
      )}
      
      {/* --- TOP TOOLBAR --- */}
      <div className="bg-[#1a3353] text-white p-1.5 flex justify-between items-center shadow-lg z-30 px-4 shrink-0">
        <div className={`flex items-center gap-4 ${isRTL ? 'mr-2' : 'ml-2'}`}>
          <button onClick={() => navigate(`/incidents${location.search}`)} className="p-1.5 hover:bg-white/10 rounded-full transition-colors text-blue-200">
            {isRTL ? <ChevronRight size={18} /> : <ArrowLeft size={18}/>}
          </button>
          
          {!isNew && (
            <div className={`flex items-center bg-black/20 border border-white/10 rounded-md px-1 py-0.5 gap-1 `}>
              <button disabled={!nav.prev} onClick={() => goTo(nav.first)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.prev} onClick={() => goTo(nav.prev)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <div className="px-2 text-[10px] font-mono border-x border-white/10 mx-1">
                <span className="text-orange-400 font-black">{nav.current_pos}</span> / {nav.total}
              </div>
              <button disabled={!nav.next} onClick={() => goTo(nav.next)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.next} onClick={() => goTo(nav.last)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
            </div>
          )}

          <div className="flex flex-col ml-4">
              <span className="text-[0.9em] text-orange-400 font-bold uppercase tracking-widest">{t('incidents.details.ticket', 'Incident Ticket')}</span>
              {/* 🎯 إظهار رقم البلاغ أو كلمة NEW */}
              <h1 className="text-sm font-black tracking-tighter">{incident?.ticket_id || t('common.new', 'NEW TICKET')}</h1>
          </div>
        </div>

        <div className={`flex items-center gap-2 ${isRTL ? 'ml-2' : 'mr-2'}`}>
          {((isNew && canCreate) || (!isNew && canUpdate)) && (
          <button onClick={handleSave} className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded-sm flex items-center gap-2 text-[11px] font-black shadow-md transition-all uppercase">
            <Save size={14}/> {t('common.save', 'SAVE CHANGES')}
          </button>
          )}
          
          {!isNew && canDelete && (
            <button onClick={() => handleDelete(id, incident?.ticket_id)} className="p-2 hover:bg-red-500/20 text-red-400 rounded transition-colors" title={t('common.delete', 'Delete')}>
              <Trash2 size={16}/>
            </button>
          )}

          {wfContext.can_start && !['COMP', 'CANC', 'CLOSED', 'CLOSE', 'CAN'].includes(incident?.status) && (
            <button 
                onClick={handleRouteWorkflow} 
                className="bg-orange-600 hover:bg-orange-700 text-white border-0 flex items-center gap-2 text-[11px] font-black h-8 shadow-md px-4 rounded-sm transition-all uppercase"
            >
                <Zap size={14} fill="currentColor" /> {t('common.route_wf', 'ROUTE WORKFLOW')}
            </button>
          )}
          
          {!isNew && (
            <WorkflowActionModal 
                moduleName="INCIDENT" refId={id} currentStatus={incident?.status} onActionComplete={fetchIncident} 
            />
          )}

          {!isNew && (
            <button onClick={() => setIsWFHistoryOpen(true)} className="p-2 hover:bg-white/10 rounded text-blue-200" title={t('common.wf_history', 'Workflow History')}>
              <Workflow size={16} className="text-orange-400"/>
            </button>
          )}

          {/* 🎯 زرار المرفقات الجديد */}
          {!isNew && (
            <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 hover:bg-white/10 rounded text-blue-200" title={t('common.attachments', 'Attachments')}>
              <Paperclip size={16} className="text-gray-300" />
              {count > 0 && <span className="absolute top-1 right-1 bg-red-600 text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black animate-pulse">{count}</span>}
            </button>
          )}

          <div className="h-6 w-[1px] bg-white/10 mx-1"></div>
          <FontControls />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-5xl mx-auto grid grid-cols-12 gap-6">
          
          {/* Main Info Card */}
          <div className="col-span-8 space-y-6">
            <div className="bg-white p-6 shadow-sm border-t-2 border-orange-500 rounded-sm">
              <h3 className={`text-[#005495] font-black uppercase mb-4 text-[0.9em] flex items-center gap-2 border-b pb-2 `}>
                <FileText size={14}/> {t('incidents.details.general', 'General Description')}
              </h3>
              <div className="space-y-4">
                <div className="flex flex-col gap-1">
                  <label className="text-gray-400 font-bold uppercase text-[0.8em]">{t('assets.headers.desc', 'Summary')}</label>
                  <input 
                    className={`text-sm font-bold text-gray-700 border-b border-gray-100 outline-none focus:border-blue-500 pb-1 ${isRTL ? 'text-right' : 'text-left'}`}
                    value={incident?.description || ''}
                    onChange={(e) => setIncident({...incident, description: e.target.value})}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-gray-400 font-bold uppercase text-[0.8em]">{t('incidents.details.detailed', 'Detailed Details')}</label>
                  <textarea 
                    rows="5"
                    className={`bg-gray-50 border border-gray-100 rounded p-3 outline-none focus:ring-1 focus:ring-blue-500 transition-all ${isRTL ? 'text-right' : 'text-left'}`}
                    value={incident?.long_description || ''}
                    onChange={(e) => setIncident({...incident, long_description: e.target.value})}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar Context */}
          <div className="col-span-4 space-y-4">
            <div className="bg-white p-4 shadow-sm border rounded-sm">
              <h3 className={`font-black uppercase text-[9px] text-gray-400 mb-3 tracking-widest border-b pb-1 ${isRTL ? 'text-right' : ''}`}>
                {t('incidents.details.context', 'Context & Status')}
              </h3>
              <div className="space-y-4">
                <StatusItem icon={<Activity size={14}/>} label={t('assets.headers.status', 'Current Status')} value={incident?.status} isBadge isRTL={isRTL} />
                
                {/* 🎯 التعديل هنا: استخدام handleSmartSearch بدلاً من setLookup المباشر */}
                <StatusItem 
                  icon={<Box size={14}/>} 
                  label={t('incidents.details.linked_asset', 'Linked Asset')} 
                  value={incident?.assetnum || '---'} 
                  isRTL={isRTL} 
                  onSearch={() => handleSmartSearch('assetnum', t('incidents.lookup.asset', 'Select Linked Asset'), 'assets')} 
                />
                
                {/* 🎯 والتعديل هنا كمان */}
                <StatusItem 
                  icon={<MapPin size={14}/>} 
                  label={t('common.location', 'Location')} 
                  value={incident?.location_id || '---'} 
                  isRTL={isRTL} 
                  onSearch={() => handleSmartSearch('location_id', t('incidents.lookup.loc', 'Select Location'), 'locations')} 
                />
                
                <StatusItem icon={<Briefcase size={14}/>} label={t('incidents.details.workorder', 'Workorder')} value={incident?.wonum || '---'} isRTL={isRTL} />
                
                <div className="border-t border-gray-100 pt-3 mt-3">
                  <StatusItem icon={<User size={14}/>} label={t('incidents.fields.reported_by', 'Reported By')} value={incident?.reported_by || '---'} isRTL={isRTL} />
                  <StatusItem icon={<Clock size={14}/>} label={t('common.date', 'Report Date')} value={new Date(incident?.report_date).toLocaleString()} isRTL={isRTL} />
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

// --- Helper Component ---
const StatusItem = ({ icon, label, value, isBadge, onSearch, isRTL }) => (
  <div className={`flex flex-col gap-1 `}>
    <div className={`flex items-center gap-2 text-gray-400 font-bold uppercase text-[0.9em] `}>
      {icon} {label}
    </div>
    <div className={`font-black uppercase flex items-center gap-2  ${isBadge ? 'bg-orange-100 text-orange-700 px-2 py-0.5 rounded-sm text-[0.9em]' : 'text-[#1a3353] text-[0.9em]'}`}>
      {value}
      {onSearch && <Search size={13} className="text-blue-400 cursor-pointer hover:text-blue-800 transition-colors" onClick={onSearch} />}
    </div>
  </div>
);

export default IncidentDetails;